import React, {useRef} from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useTheme} from "@mui/material";
import PageTitle from "shared/ui/PageTitle";
import BoxContent from "shared/ui/BoxContent";
import Typography from "@mui/material/Typography";
import CustomButton from "shared/ui/CustomButton";
import {iFrame, TUTORIAL_VIDEOS} from "../../assistance/pages/Tutorials";

const schoolInstructions = [
    {
        id: "1",
        title: "1. Creare cont",
        content: <>
            <Typography variant="body1"><b>Creare cont de școală</b>: brio.school</Typography>
            <Typography variant="body1">a) în situația în care Contul de Școală va fi înregistrat și utilizat de
                către unitatea de învățământ, este necesară o adresă de e-mail oficială a acesteia (diferită de cea
                pe care o va folosi profesorul în testarea la clasă).
            </Typography>
            <Typography variant="body1">b) adresa de e-mail va fi apoi validată pentru autentificarea în contul
                creat (accesați link-ul din e-mail pentru activarea contului).
            </Typography>
        </>,
    },
    {
        id: "2",
        title: "2. Autentificare",
        content: <Typography variant="body1">Vă autentificați pe brio.school cu școala pe care a fost deschis contul,
            adresa de e-mail și parola folosite la crearea acestuia.
        </Typography>,
    },
    {
        id: "3",
        title: "3. Adăugare clase, profesori, elevi",
        content: <>
            <Typography variant="body1">După autentificarea în contul creat, adăugați clasele, profesorii și elevii din
                școală (datele pot fi importate automat prin opțiunea „Importă” din meniu).
            </Typography>
        </>,
    },
    {
        id: "4",
        title: "4. Validare adrese de e-mail",
        content: <>
            <Typography variant="body1">a) Profesorul adăugat în listă va primi pe adresa de e-mail o confirmare și
                o parolă de autentificare (cont de profesor) cu care se poate conecta pe brio.school.
            </Typography>
            <Typography variant="body1">b) Elevii adăugați în clase vor primi pe adresa de e-mail un link de
                validare pentru accesarea contului de elev; din care vor putea accesa apoi testele generate de
                profesori.
            </Typography>
        </>,
    },
    {
        id: "5",
        title: "5. Generare teste",
        content: <>
            <Typography variant="body1">După adăugarea profesorilor, claselor și elevilor (meniul din stânga) și
                validarea adreselor de e-mail ale acestora, le vor putea fi generate elevilor testele dorite.
            </Typography>
            <Typography variant="body1">a) Testele pot fi generate DOAR din conturile de profesor.</Typography>
            <Typography variant="body1">b) Testele generate vor putea fi efectuate de către elevi prin accesarea
                linkului transmis pe e-mail sau direct din contul de elev prin autentificarea în prealabil.
            </Typography>
            <Typography variant="body1">c) Testele pot fi generate după selectarea în prealabil a clasei, materiei,
                capitolelor de materie și clasei de studiu a materiei.
            </Typography>
        </>,
    },
    {
        id: "6",
        title: "6. Rezultate",
        content: <>
            <Typography variant="body1">a) Raportul Brio va putea fi consultat după finalizarea testului, împreună
                cu pagina de răspunsuri (unde sunt marcate răspunsurile greșite/corecte/omise) și scorul Brio de
                către elevi/părinți, profesori și director.
            </Typography>
            <Typography variant="body1">b) Aveți posibilitatea de a descărca un raport exhaustiv la nivel de școală
                (la sfârșitul anului școlar) pe baza rezultatelor tuturor elevilor testați la toate materiile, care
                vă va arăta nivelul real al acestora, precum și capitolele unde au nevoie de aprofundare.
            </Typography>
            <Typography variant="body1">c) Aveți posibilitatea de a descărca un raport complet la nivel de clasă
                care să vă arate principalele zone ce necesită îmbunătățirea pe anumite capitole de materie.
            </Typography>
            <Typography variant="body1">d) Secțiunea „Statistici” vă va arăta evoluția săptămânală a elevilor
                testați, precum și o imagine de ansamblu asupra nivelului școlii.
            </Typography>
        </>,
    },
]

const teacherInstructions = [
    {
        id: "1",
        title: "1. Creare cont",
        content: <>
            <Typography variant="body1"><b>Creare cont de profesor</b>: brio.school</Typography>
            <Typography variant="body1">a) Vă înregistrați cu o adresă de e-mail (personală/instituțională) reală, pe
                baza căreia vă veți autentifica apoi în contul creat.
            </Typography>
            <Typography variant="body1">b) Adresa de e-mail va fi apoi validată pentru autentificarea în contul creat
                (accesați link-ul din e-mail pentru activarea contului).
            </Typography>
        </>,
    },
    {
        id: "2",
        title: "2. Autentificare",
        content: <Typography variant="body1">Vă autentificați pe brio.school cu adresa de e-mail și parola folosite la
            crearea contului.
        </Typography>,
    },
    {
        id: "3",
        title: "3. Adăugare clase, elevi",
        content: <>
            <Typography variant="body1">După autentificarea în contul creat, adăugați clasele și elevii cărora doriți să
                le generați teste (datele pot fi importate automat prin opțiunea „Importă” din meniu).
            </Typography>
        </>,
    },
    {
        id: "4",
        title: "4. Validare adrese de e-mail",
        content:
            <Typography variant="body1">
                Elevii adăugați în clase vor primi pe adresa de e-mail un link de validare pentru accesarea contului de
                elev; din acest cont (sau pe e-mail) vor putea accesa apoi testele generate de profesor.
            </Typography>
    },
    {
        id: "5",
        title: "5. Generare teste",
        content: <>
            <Typography variant="body1">După adăugarea claselor și elevilor (meniul din stânga) și validarea adreselor
                de e-mail ale acestora, le vor putea fi generate testele dorite.
            </Typography>
            <Typography variant="body1">a) Testele pot fi generate DOAR din conturile de profesor.</Typography>
            <Typography variant="body1">b) Testele generate vor putea fi efectuate de către elevi prin accesarea
                linkului transmis pe e-mail sau direct din contul de elev prin autentificarea în prealabil.
            </Typography>
            <Typography variant="body1">c) Testele pot fi generate după selectarea în prealabil a clasei, materiei,
                capitolelor de materie și clasei de studiu a materiei.
            </Typography>
        </>,
    },
    {
        id: "6",
        title: "6. Rezultate",
        content: <>
            <Typography variant="body1">a) Raportul Brio va putea fi consultat după finalizarea testului, împreună
                cu pagina de răspunsuri (unde sunt marcate răspunsurile greșite/corecte/omise) și scorul Brio de
                către elevi/părinți, profesori și director.
            </Typography>
            <Typography variant="body1">b) Aveți posibilitatea de a descărca un raport exhaustiv la nivel de școală
                (la sfârșitul anului școlar) pe baza rezultatelor tuturor elevilor testați la toate materiile, care
                vă va arăta nivelul real al acestora, precum și capitolele unde au nevoie de aprofundare.
            </Typography>
            <Typography variant="body1">c) Aveți posibilitatea de a descărca un raport complet la nivel de clasă
                care să vă arate principalele zone ce necesită îmbunătățirea pe anumite capitole de materie.
            </Typography>
            <Typography variant="body1">d) Secțiunea „Statistici” vă va arăta evoluția săptămânală a elevilor
                testați, precum și o imagine de ansamblu asupra nivelului școlii.
            </Typography>
        </>,
    },
];

const buttonsData = {
    school: [
        {label: "1. Creare cont", index: 1},
        {label: "2. Autentificare", index: 2},
        {label: "3. Adăugare clase, profesori, elevi", index: 3},
        {label: "4. Validare adrese de e-mail", index: 4},
        {label: "5. Generare teste", index: 5},
        {label: "6. Rezultate", index: 6},
    ],
    teacher: [
        {label: "1. Creare cont", index: 8},
        {label: "2. Autentificare", index: 9},
        {label: "3. Adăugare clase, elevi", index: 10},
        {label: "4. Validare adrese de e-mail", index: 11},
        {label: "5. Generare teste", index: 12},
        {label: "6. Rezultate", index: 13},
    ],
    tutorials: [
        {label: "1. Generare test școlar", index: 16},
        {label: "2. Generare test antrenament", index: 17},
        {label: "3. Analiză teste finalizate", index: 18},
        {label: "4. Administrare teste proprii", index: 19},
    ]
};

const Instructions = () => {
    const ref = useRef<null | HTMLDivElement>(null);
    const theme = useTheme();
    const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

    function scrollToIndex(index: number) {
        const listNode = ref.current;
        // This line assumes a particular DOM structure:
        const imgNode = listNode?.querySelectorAll('h2')[index];

        imgNode?.scrollIntoView({behavior: "smooth", block: "start", inline: "start"});
    }

    const renderButton = (label: string, index: number) => {
        return (
            <CustomButton
                key={index}
                size="large"
                buttonType="text"
                bgcolor="text"
                onClick={() => scrollToIndex(index)}
                sxExtra={{marginBottom: {xs: "5px", lg: "10px"}}}
            >
                {label}
            </CustomButton>
        );
    }

    return (
        <Box component="main">
            {!isSmUp && (
                <PageTitle>
                    Instructiuni
                </PageTitle>
            )}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={4} lg={3}>
                    <Box sx={{
                        position: {xs: "sticky", sm: "fixed"},
                        top: {xs: "120px", lg: "180px"},
                        alignSelf: "flex-start",
                        overflowY: "auto",
                        paddingRight: 2,
                        width: {sm: "30%", lg: "20%"},
                        height: {xs: "auto", sm: "calc(100vh - 120px)", lg: "calc(100vh - 270px)"}
                    }}>
                        {renderButton("I. Cont de școală/Centru de meditație", 0)}
                        {isSmUp && (
                            <Box style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                paddingLeft: "20px"
                            }}>
                                {buttonsData.school.map(({label, index}) => renderButton(label, index))}
                            </Box>
                        )}
                        <Box></Box>
                        {renderButton("II. Cont de profesor", 7)}
                        {isSmUp && (
                            <Box style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                paddingLeft: "20px"
                            }}>
                                {buttonsData.teacher.map(({label, index}) => renderButton(label, index))}
                            </Box>
                        )}
                        <Box></Box>
                        {renderButton("III. Tutoriale video", 15)}
                        {isSmUp && (
                            <Box style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                paddingLeft: "20px"
                            }}>
                                {buttonsData.tutorials.map(({label, index}) => renderButton(label, index))}
                            </Box>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12} sm={8} lg={9}>
                    {isSmUp && (
                        <PageTitle>
                            Instructiuni
                        </PageTitle>
                    )}
                    <Box ref={ref}>
                        <Typography variant="h2" mt={3} mb={3}
                                    sx={{fontSize: "1.5rem", scrollMarginTop: {xs: "100px", lg: "20px"}}}>I. Creare și
                            autentificare <b>Cont de Școală* BRIO/Centru de meditație</b></Typography>
                        <Typography variant="body1">*Contul de Școală/Centru de meditații poate fi creat/utilizat de
                            către unitatea
                            de învățământ.
                        </Typography>
                        <Typography variant="body1">În contul Brio dedicat unităților de învățământ vor putea fi
                            adăugați
                            profesorii, clasele și elevii cărora doriți să le generați teste, testele fiind generate
                            apoi exclusiv
                            din conturile de profesori asociați în contul de școală.
                        </Typography>
                        <Box ml={3}>
                            {schoolInstructions.map((item, index) => (
                                <Box key={item.id}>
                                    <Typography variant="h2" mt={3} mb={3} sx={{
                                        scrollMarginTop: {
                                            xs: "100px",
                                            lg: "20px"
                                        }
                                    }}>{item.title}</Typography>
                                    <BoxContent>
                                        {item.content}
                                    </BoxContent>
                                    {index !== schoolInstructions.length - 1 && <Box mb={3}></Box>}
                                </Box>
                            ))}
                        </Box>
                        <Typography variant="h2" mt={6} mb={3} sx={{fontSize: "1.5rem", scrollMarginTop: {xs: "100px", lg: "20px"}}}>II. Creare și
                            autentificare <b>Cont de Profesor* BRIO</b></Typography>
                        <Typography variant="body1">*Contul de Profesor poate fi creat/utilizat de către un profesor
                            exclusiv pentru
                            clasa/clasele la care predă (nu este cont instituțional).
                        </Typography>
                        <Typography variant="body1">În contul Brio dedicat profesorilor vor putea fi adăugate clasele și
                            elevii
                            cărora doriți să le generați teste.
                        </Typography>
                        <Box ml={3}>
                            {teacherInstructions.map((item, index) => (
                                <Box key={item.id}>
                                    <Typography variant="h2" mt={3} mb={3} sx={{
                                        scrollMarginTop: {xs: "100px", lg: "20px"}
                                    }}>{item.title}</Typography>
                                    <BoxContent>
                                        {item.content}
                                    </BoxContent>
                                    {index !== schoolInstructions.length - 1 && <Box mb={3}></Box>}
                                </Box>
                            ))}
                        </Box>
                        <Box ml={3}>
                        <Typography variant="h2" mt={6} mb={3} color="error.main"
                                    sx={{fontSize: "1.5rem"}}>IMPORTANT</Typography>
                        <BoxContent>
                            <ul>
                                <li>
                                    Dacă elevul/profesorul adăugat nu validează adresa de e-mail (accesând link-ul pe
                                    care îl
                                    primește automat), nu se va putea autentifica și folosi apoi funcționalitățile
                                    contului Brio.
                                </li>
                                <li>
                                    Statusul de validare al fiecărui elev/profesor apare sub forma unei buline de
                                    culoare verde
                                    (adresa de e-mail validată/ confirmată).
                                </li>
                            </ul>
                        </BoxContent>
                        </Box>
                        <Box ml={3}>
                            <Typography variant="h2" mt={6} mb={3} sx={{fontSize: "1.5rem", scrollMarginTop: {xs: "100px", lg: "20px"}}}>
                                III. Tutoriale video
                            </Typography>
                            {TUTORIAL_VIDEOS.map(({title, id}, index) => (
                                <Box key={id}>
                                    <Typography variant="h2" mt={3} mb={3} sx={{
                                        scrollMarginTop: {xs: "100px", lg: "20px"}
                                    }}>{index + 1}. {title}</Typography>
                                    <BoxContent>
                                        <Box sx={{position: "relative", width: "100%", overflow: "hidden", paddingTop: "56.25%"}}>
                                            {iFrame({videoId: id})}
                                        </Box>
                                    </BoxContent>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
};

export default Instructions;